import { findMatches, normKey, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { commit } from '../db/commit';
import { db } from '../db/db';
import type { ArticleRow } from '../db/types';
import { articleName } from './prices';
import { buildMergeMap, resolveWith } from './products';

// Artykuły z paragonów jako „drugi poziom” produktów: konkretny towar (np. „Masło Ekstra 200 g”) do przypięcia
// do pozycji listy, z ostatnią ceną. Podpowiadamy artykuły podpięte pod produkt i te o podobnej nazwie.

/** Artykuły kupione dawniej niż tyle temu nie trafiają do podpowiedzi (wycofane z oferty, stary kod). */
const RECENT_MS = 18 * 30 * 86_400_000;
const MAX_OPTIONS = 3;

export interface ArticleIndex {
  /** Świeże artykuły z ceną, bez dubli nazw (zostaje ostatnio kupiony). */
  recent: ArticleRow[];
  refs: { productId: Id; name: string; normKey: string }[];
  byProduct: Map<Id, ArticleRow[]>;
  byId: Map<Id, ArticleRow>;
  resolveProduct: (id: Id) => Id;
}

export async function loadArticleIndex(now = Date.now()): Promise<ArticleIndex> {
  const [articles, products] = await Promise.all([db.articles.toArray(), db.products.toArray()]);
  const resolveProduct = resolveWith(buildMergeMap(products));
  const byName = new Map<string, ArticleRow>();
  for (const a of articles) {
    if (a.deleted || a.lastShelfGr === null || (a.lastBoughtMs ?? 0) < now - RECENT_MS) continue;
    const key = articleName(a).toLowerCase();
    const prev = byName.get(key);
    if (!prev || (prev.lastBoughtMs ?? 0) < (a.lastBoughtMs ?? 0)) byName.set(key, a);
  }
  const recent = [...byName.values()].sort((a, b) => (b.lastBoughtMs ?? 0) - (a.lastBoughtMs ?? 0));
  const byProduct = new Map<Id, ArticleRow[]>();
  for (const a of recent) {
    if (!a.productId) continue;
    const pid = resolveProduct(a.productId);
    byProduct.set(pid, [...(byProduct.get(pid) ?? []), a]);
  }
  return {
    recent,
    refs: recent.map((a) => ({ productId: a.id, name: articleName(a), normKey: normKey(articleName(a)) })),
    byProduct,
    byId: new Map(articles.map((a) => [a.id, a])),
    resolveProduct,
  };
}

export function useArticleIndex(enabled = true): ArticleIndex | undefined {
  return useLiveQuery(async () => (enabled ? loadArticleIndex() : undefined), [enabled]);
}

/** Konkretne towary dla pozycji: podpięte pod jej produkt, potem o podobnej nazwie. */
export function articleOptions(index: ArticleIndex, key: string, productId: Id | null, max = MAX_OPTIONS): ArticleRow[] {
  const out: ArticleRow[] = [];
  const add = (a: ArticleRow | undefined) => {
    if (a && !out.some((o) => o.id === a.id) && out.length < max) out.push(a);
  };
  if (productId) for (const a of index.byProduct.get(index.resolveProduct(productId)) ?? []) add(a);
  if (key) {
    const m = findMatches(key, index.refs, []);
    if (m.exact) add(index.byId.get(m.exact));
    for (const c of m.candidates) if (c.score >= 0.65) add(index.byId.get(c.productId));
  }
  return out;
}

/** Przypina konkretny towar do pozycji listy (null = odpina). Artykuł bez produktu uczy się produktu pozycji. */
export async function setItemArticle(itemId: Id, articleId: Id | null): Promise<void> {
  await commit(async (w) => {
    const item = await db.listItems.get(itemId);
    if (!item) return;
    await w.set('listItems', itemId, { articleId });
    if (!articleId || !item.productId) return;
    const a = await db.articles.get(articleId);
    if (a && !a.productId) await w.set('articles', articleId, { productId: item.productId, linkSource: 'pin' });
  });
}

/** Podpina artykuł pod produkt Odhaczki (decyzja użytkownika); null = artykuł bez produktu. */
export async function setArticleProduct(articleId: Id, productId: Id | null): Promise<void> {
  await commit((w) => w.set('articles', articleId, { productId, linkSource: productId ? 'user' : 'none' }));
}
